import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
        email,
        password,
      });

      if (response.data.status) {
        const { token, id_usuario } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("id_usuario", id_usuario);
        localStorage.setItem("estadoPartesMostrar", "1");
        window.location.href = "/Partes";
      }
    } catch {
      setError("Credenciales incorrectas. Verifica tu email y contraseña.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* Panel izquierdo — branding */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
          background: "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Círculos decorativos */}
        <Box
          sx={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.1)",
            top: -100,
            right: -100,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.08)",
            bottom: -80,
            left: -80,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
            top: "30%",
            left: "10%",
          }}
        />

        <Box sx={{ position: "relative", textAlign: "center", px: 6 }}>
          <img
            src="https://api.sat.cloudlsf.es/imagenes/logoLsfTransparente.png"
            alt="Logo LSF"
            style={{
              maxWidth: 180,
              marginBottom: 8,
              filter: "brightness(0) invert(1)",
              clipPath: "inset(0 0 28% 0)",
            }}
          />
          <Typography variant="h4" sx={{ color: "white", fontWeight: 700, mb: 2 }}>
            Servicio Asistencia Técnica
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.7)", maxWidth: 320 }}>
            Plataforma de gestión de servicios técnicos y seguimiento de partes de trabajo.
          </Typography>
        </Box>
      </Box>

      {/* Panel derecho — formulario */}
      <Box
        sx={{
          flex: { xs: 1, md: "0 0 480px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: { xs: 3, sm: 6 },
          background: "#ffffff",
        }}
      >
        {/* Logo solo en móvil */}
        <Box sx={{ display: { xs: "block", md: "none" }, mb: 4, textAlign: "center" }}>
          <img
            src="https://api.sat.cloudlsf.es/imagenes/logoLsfTransparente.png"
            alt="Logo LSF"
            style={{ maxWidth: 140, clipPath: "inset(0 0 28% 0)" }}
          />
        </Box>

        <Box sx={{ width: "100%", maxWidth: 380 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1a237e", mb: 1 }}>
            Bienvenido
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
            Inicia sesión para acceder a tu cuenta
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin} noValidate>
            <TextField
              label="Correo electrónico"
              type="email"
              variant="outlined"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": { borderColor: "#1a237e" },
                  "&.Mui-focused fieldset": { borderColor: "#1a237e" },
                },
                "& label.Mui-focused": { color: "#1a237e" },
              }}
            />

            <TextField
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": { borderColor: "#1a237e" },
                  "&.Mui-focused fieldset": { borderColor: "#1a237e" },
                },
                "& label.Mui-focused": { color: "#1a237e" },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: "1rem",
                background: "linear-gradient(135deg, #1a237e, #0d47a1)",
                boxShadow: "0 4px 15px rgba(26, 35, 126, 0.35)",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #0d47a1, #01579b)",
                  boxShadow: "0 6px 20px rgba(26, 35, 126, 0.45)",
                },
                "&:disabled": { background: "#bdbdbd" },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Acceder"}
            </Button>
          </Box>
        </Box>

        <Typography variant="caption" sx={{ color: "text.disabled", mt: 6 }}>
          © {new Date().getFullYear()} LSF Informática · Todos los derechos reservados
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
